import { useGet } from "@albertine/api-client";
import type { MemberDataFirestoreRecord, Supplier } from "@albertine/types";
import { useEffect, useState } from "react";
import { Route, useHistory, useRouteMatch } from "react-router";
import type { Agent } from "../../common/types/Agent";
import type { Booking } from "../../common/types/Booking";
import type { Conversation } from "../../common/types/Conversation";
import type { Request } from "../../common/types/Request";
import { excludeUndefined } from "../../common/utils/array.util";
import useGlobalState from "../../state/GlobalStateHook";
import {
    defaultPanelUiState,
    type PanelUiState,
    panelUiStateKey,
} from "../../state/PanelUiState";
import { mapApiBookingToBooking } from "../../utils/booking.adapter";
import routes, { type CatalogContext, routeSchemas } from "../../utils/routes";
import EditBookingForm from "../Bookings/EditBookingForm";
import Catalog from "../CatalogBeta/Catalog";
import Cart from "../CatalogBeta/ShoppingCart/Cart";
import ProactivityCart from "../CatalogBeta/ShoppingCart/ProactivityCart";
import ViewCatalogItem from "../CatalogBeta/ViewCatalogItem";
import WithGoogle from "../CatalogBeta/WithGoogle";
import Chat from "../Chat/Chat";
import { createContextKey, getCurrentCatalogItems } from "../Chat/ChatContext";
import FullScreenOverlay from "../Common/Overlay/FullScreenOverlay";
import { MemberProfileHost } from "../MemberData/MemberProfileHost";
import MemberHeader from "../MemberHeader/MemberHeader";
import RequestView from "../RequestView/RequestView";

type Props = {
    currentAgent: Agent;
    member: MemberDataFirestoreRecord;
    conversation: Conversation;
    request: Request;
    suppliers: Supplier[];
};

const MemberWithSelectedRequestView = function MemberWithSelectedRequestView(
    props: Props,
) {
    const { currentAgent, member, conversation, request, suppliers } = props;

    const isArticlesRoute = useRouteMatch("/member/:memberId/articles/");
    const isArticleCategoriesRoute = useRouteMatch(
        "/member/:memberId/articles/category/:category",
    );
    const matchRequestCatalog = useRouteMatch<{
        catalogId: string;
        mode: "edit" | "create" | "view";
        context: CatalogContext;
    }>(routeSchemas.requestCatalog);
    const matchRequestBooking = useRouteMatch<{
        bookingId: string;
        requestId: string;
    }>(routeSchemas.requestBooking);
    const history = useHistory();
    const { data: bookings } = useGet(
        "bookings",
        { query: { requestId: request.id } },
        {
            select: (response) => response.bookings.map(mapApiBookingToBooking),
        },
    );
    const [editedBooking, setEditedBooking] = useState<Booking | undefined>();

    useEffect(() => {
        if (matchRequestBooking?.params.bookingId) {
            const myBooking = bookings?.find(
                (it) => it.id === matchRequestBooking.params.bookingId,
            );
            setEditedBooking(myBooking);
        } else {
            setEditedBooking(undefined);
        }
    }, [bookings, matchRequestBooking]);

    const { globalState: panelState, setGlobalState: setPanelState } =
        useGlobalState<PanelUiState>(panelUiStateKey, {
            ...defaultPanelUiState,
            activeTab:
                isArticlesRoute || isArticleCategoriesRoute ? "articles" : "profile",
        });

    return (
        <>
            <div className="row">
                <div className="column">
                    <MemberHeader
                        member={member}
                        collapsed={panelState.isCollapsed}
                        toggleClicked={() => {
                            setPanelState({
                                ...panelState,
                                isCollapsed: !panelState.isCollapsed,
                            });
                        }}
                        showTopicFilter
                    />
                    <div className="row">
                        <div className="tab request-tab">
                            <RequestView
                                request={request}
                                currentAgent={currentAgent}
                                memberTenant={member.tenant}
                            />
                        </div>
                        <div className="tab">
                            {conversation && currentAgent && member && (
                                <Chat
                                    conversation={conversation}
                                    request={request}
                                    currentAgent={currentAgent}
                                    member={member}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {!panelState.isCollapsed && (
                    <MemberProfileHost
                        currentAgent={currentAgent}
                        memberId={member.id}
                    />
                )}
            </div>

            {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
            <Route
                path={[
                    routeSchemas.requestCatalogContext("service"),
                    routeSchemas.requestCatalogContext("recommendations"),
                ]}
            >
                <FullScreenOverlay
                    bucket={({ size, minimize, maximize }) => (
                        <Cart
                            size={size}
                            minimize={minimize}
                            maximize={maximize}
                            request={request}
                            bookings={bookings || []}
                        />
                    )}
                >
                    <WithGoogle>
                        {(googlePlaceService) => (
                            <Catalog
                                currentAgent={currentAgent}
                                googlePlaceService={googlePlaceService}
                                suppliers={suppliers || []}
                                request={request}
                                member={member}
                                cartItems={(bookings || [])
                                    .map((it) => it.catalogItem || undefined)
                                    .filter(excludeUndefined)}
                                standalone={false}
                            />
                        )}
                    </WithGoogle>
                </FullScreenOverlay>
            </Route>

            {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
            <Route path={routeSchemas.requestCatalogContext("proactivity")}>
                <FullScreenOverlay
                    bucket={({ size, minimize, maximize }) => (
                        <ProactivityCart
                            initialItems={getCurrentCatalogItems(
                                createContextKey(member, request),
                            )}
                            request={request}
                            member={member}
                            size={size}
                            minimize={minimize}
                            maximize={maximize}
                        />
                    )}
                >
                    <WithGoogle>
                        {(googlePlaceService) => (
                            <Catalog
                                currentAgent={currentAgent}
                                googlePlaceService={googlePlaceService}
                                suppliers={suppliers}
                                request={request}
                                member={member}
                                cartItems={getCurrentCatalogItems(
                                    createContextKey(member, request),
                                ).map((it) => it.id)}
                                standalone={false}
                            />
                        )}
                    </WithGoogle>
                </FullScreenOverlay>
            </Route>

            {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
            <Route path={routeSchemas.requestBooking}>
                {matchRequestBooking?.params.bookingId &&
                    editedBooking !== undefined && (
                        <EditBookingForm
                            booking={editedBooking}
                            request={request}
                            member={member}
                            currentAgent={currentAgent}
                            suppliers={suppliers}
                        />
                    )}
            </Route>

            {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
            <Route path={routeSchemas.requestCatalogContext("browse")}>
                {matchRequestCatalog?.params.catalogId && (
                    <FullScreenOverlay>
                        <WithGoogle>
                            {(googlePlaceService) => (
                                <ViewCatalogItem
                                    catalogItemId={matchRequestCatalog.params.catalogId}
                                    currentAgent={currentAgent}
                                    googlePlaceService={googlePlaceService}
                                    suppliers={suppliers}
                                    onClose={() => {
                                        history.push(routes.request(member.id));
                                    }}
                                    onToggleArchive={() => {
                                        history.push(routes.request(member.id));
                                    }}
                                />
                            )}
                        </WithGoogle>
                    </FullScreenOverlay>
                )}
            </Route>
        </>
    );
};

export default MemberWithSelectedRequestView;
